// NPM Imports
import moment from "moment";
import { Toastr } from "../revvComponents";

// Internal Imports
import { GRID } from "../constants";
import { GlobalUtils, ProgramUtils } from "../utils";

// Destructuring
const {
  DEALTABLE_COLUMNS,
  GRID_PAGE_TYPE,
  PRICEBOOK_COLUMNS,
  PRICING_METHODS,
  PRICING_METHODS_LABELS,
  PRODUCTS_COLUMNS,
  RECURRENCE,
} = GRID;
const { numberForamting } = GlobalUtils;
const { sortArrayAccToMap } = ProgramUtils;

// Declarations
const intRegex = /^[0-9\b]+$/;
const floatRegex = /^([0-9]\d*)(\.[0-9]*?)?$/;

/*
==========================================
            Helper Functions
==========================================
*/

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _formatDate = params => moment(params.value).format("L");

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _formatCurrency = params =>
  params.value
    ? numberForamting(params.value, params.colDef.currencySymbol)
    : `${params.colDef.currencySymbol}0`;

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _formatPercentage = params => `${params.value}%`;

/**
 * @param {string/number} value - Contains value entered by user.
 */
const _isInteger = value => intRegex.test(value);

/**
 * @param {string/number} value - Contains value entered by user.
 */
const _isFloat = value => floatRegex.test(value);

/**
 * @param {object} params - Contains single row data from agGrid.
 * @param {boolean} forClassName - Flag to check between classname & cell editable.
 */
const _isQtyEditable = params => {
  const colDefs = params.columnApi.columnController.columnDefs;

  if (
    (params.data.metaData && params.data.metaData.value) ||
    params.data.metaData.type === PRICING_METHODS.NON_LINEAR ||
    params.data.metaData.type === PRICING_METHODS.DIY_PRICING
  ) {
    const qtyEditableField = colDefs.find(
      col => col.name === DEALTABLE_COLUMNS.QUANTITY_EDITABLE,
    ).field;
    return params.data[qtyEditableField];
  }
  if (params.data.metaData.isAdhoc) {
    return true;
  }
  return !params.data.metaData || params.data.metaData.value;
};

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _isSellingEditable = params => {
  const colDefs = params.columnApi.columnController.columnDefs;
  const sellingEditableField = colDefs.find(
    col => col.name === DEALTABLE_COLUMNS.IS_SELLINGTERM_EDITABLE,
  ).field;
  return params.data[sellingEditableField];
};

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _isBillingFrequencyEditable = params => {
  const colDefs = params.columnApi.columnController.columnDefs;
  const billingFrequencyEditableField = colDefs.find(
    col => col.name === DEALTABLE_COLUMNS.BILLING_FREQUENCY_EDITABLE,
  ).field;
  return params.data[billingFrequencyEditableField];
};

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _isSkuQtyEditable = params => {
  const colDefs = params.columnApi.columnController.columnDefs;
  const skuQtyEditableField = colDefs.find(
    col => col.name === DEALTABLE_COLUMNS.SKU_QUANTITY_EDITABLE,
  ).field;
  return params.data[skuQtyEditableField];
};

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _isBasePriceEditable = params =>
  params.data.metaData.type === PRICING_METHODS.DIY_PRICING;

/**
 * @param {object} params - Contains single row data from agGrid.
 */
const _setIntegerValue = params => {
  const {
    data,
    column: {
      columnApi: { columnController: { columnDefs = {} } = {} } = {},
    } = {},
  } = params;
  const newValue = parseFloat(params.newValue);
  const { field, name, props } = params.colDef;
  const range = { ...props.range }; // Direct min range value update leads to mutation.

  /**
   * Logic to Handle where Min Qty validation should be applied (i.e qty or sku_qty column)
   */

  const qtyUom =
    data[columnDefs.find(col => col.name === "quantity_uom").field];

  if (
    (name === "quantity" &&
      ([
        PRICING_METHODS.TIER_PRICING,
        PRICING_METHODS.STEP_TIER_PRICING,
        PRICING_METHODS.VOLUME_PRICING,
      ].includes(data.metaData.type) ||
        (data.metaData.type === PRICING_METHODS.FLAT_PRICE && qtyUom))) ||
    (name === "sku_quantity" &&
      ([
        PRICING_METHODS.BUNDLE_PRICING,
        PRICING_METHODS.NON_LINEAR,
        PRICING_METHODS.PERCENTAGE_OF_TOTAL,
        PRICING_METHODS.DIY_PRICING,
      ].includes(data.metaData.type) ||
        (data.metaData.type === PRICING_METHODS.FLAT_PRICE && !qtyUom)))
  ) {
    const minimumQty =
      data[columnDefs.find(col => col.name === "min_quantity").field] || 0;
    if (props && range && minimumQty) {
      range.min = minimumQty;
    }
  }

  /**
   * Logic to Handle Min Selling Term validation
   */
  if (name === "selling_term") {
    const minSellingTerm =
      data[columnDefs.find(col => col.name === "selling_term").field] || 0;
    if (props && range && minSellingTerm) {
      range.min = minSellingTerm;
    }
  }

  // If it has min & max range
  if (
    _isInteger(newValue) &&
    newValue !== params.oldValue &&
    range &&
    newValue >= range.min &&
    newValue <= range.max
  ) {
    params.data[field] = newValue;
    return true;
  }
  // If it has no range
  if (
    _isInteger(newValue) &&
    newValue !== params.oldValue &&
    Object.keys(range).length === 0
  ) {
    params.data[field] = parseInt(newValue, 10);
    return true;
  }

  if (newValue !== params.oldValue) {
    Toastr.showToastr({
      type: "warning",
      message: `Enter value greater or equal to ${range.min}`,
      timeout: 3000,
    });
  }

  return false;
};

/**
 * @param {object} params - Contains single row data from agGrid.
 */
export const _setFloatValue = params => {
  const { data } = params;
  const { field, name, props } = params.colDef;
  const newValue = parseFloat(params.newValue);

  const range = { ...props.range }; // Direct min range value update leads to mutation.

  if (
    data.metaData.type === PRICING_METHODS.DIY_PRICING &&
    name === DEALTABLE_COLUMNS.EXT_BASE_PRICE
  ) {
    range.min = data.metaData.minPrice;
  }

  // If it has min & max range
  if (
    _isFloat(newValue) &&
    newValue !== params.oldValue &&
    range &&
    newValue >= range.min &&
    newValue <= range.max
  ) {
    params.data[field] = parseFloat(newValue, 10);
    return true;
  }
  // If it has no range
  if (
    _isFloat(newValue) &&
    newValue !== params.oldValue &&
    Object.keys(range).length === 0
  ) {
    params.data[field] = parseFloat(newValue, 10);
    return true;
  }

  if (newValue !== params.oldValue) {
    Toastr.showToastr({
      type: "warning",
      message: `Enter value greater or equal to ${range.min}`,
      timeout: 3000,
    });
  }

  return false;
};

/*
==========================================
            Mutation Functions
==========================================
*/

/**
 * TODO:: Relook the below logic
 * @param {object} params - Contains single row data from agGrid.
 */
export const isCellEditable = params => {
  const fieldName = params.colDef.name;
  switch (fieldName) {
    case DEALTABLE_COLUMNS.QUANTITY:
      return _isQtyEditable(params);
    case DEALTABLE_COLUMNS.SELLING_TERM:
      return _isSellingEditable(params);
    case DEALTABLE_COLUMNS.BILLING_FREQUENCY:
      return _isBillingFrequencyEditable(params);
    case DEALTABLE_COLUMNS.SKU_QUANTITY:
      return _isSkuQtyEditable(params);
    case DEALTABLE_COLUMNS.PRICE: // Points to Base Price
    case DEALTABLE_COLUMNS.EXT_BASE_PRICE:
      return _isBasePriceEditable(params);
    default:
      return null;
  }
};

/**
 * TODO:: Relook the below logic
 * @param {object} params - Contains single row data from agGrid.
 */
export const setClassName = params => {
  const fieldName = params.colDef.name;
  const columnName = `col-${fieldName.split("_").join("-")}`;
  const CELL_EDITABLE_CLASSNAME = ["rs-cell-editable", columnName];
  let isEditable;
  switch (fieldName) {
    case DEALTABLE_COLUMNS.QUANTITY: {
      isEditable = _isQtyEditable(params);
      return isEditable || isEditable === undefined
        ? CELL_EDITABLE_CLASSNAME
        : columnName;
    }
    case DEALTABLE_COLUMNS.SELLING_TERM:
      isEditable = _isSellingEditable(params);
      return isEditable ? CELL_EDITABLE_CLASSNAME : columnName;
    case DEALTABLE_COLUMNS.BILLING_FREQUENCY: {
      isEditable = _isBillingFrequencyEditable(params);
      return isEditable ? CELL_EDITABLE_CLASSNAME : columnName;
    }
    case DEALTABLE_COLUMNS.SKU_QUANTITY: {
      isEditable = _isSkuQtyEditable(params);
      return isEditable ? CELL_EDITABLE_CLASSNAME : columnName;
    }
    case DEALTABLE_COLUMNS.PRICE: // Points to Base Price
    case DEALTABLE_COLUMNS.EXT_BASE_PRICE:
      isEditable = _isBasePriceEditable(params);
      return isEditable ? CELL_EDITABLE_CLASSNAME : columnName;
    default:
      return null;
  }
};

/**
 * @param {object} item - API Column Data.
 * @param {object} object - AgGrid Column Object.
 */
export const generateBasedOnType = (item, object) => {
  const type = item.field.type.toLowerCase();
  const obj = object;
  obj.minWidth = 80;
  switch (type) {
    case "checkboxfield":
      obj.minWidth = 70;
      obj.cellEditor = "CheckBoxEditor";
      obj.cellRenderer = "CheckBoxRenderer";
      return obj;
    case "currencyfield":
      obj.valueFormatter = _formatCurrency;
      obj.valueSetter = function (params) {
        if (params.data.metaData !== undefined) {
          return params.data.metaData.type === PRICING_METHODS.DIY_PRICING
            ? _setFloatValue(params)
            : _setIntegerValue(params);
        }
        return _setIntegerValue;
      };
      return obj;
    case "datefield":
      obj.valueFormatter = _formatDate;
      obj.cellEditor = "AgDatePicker";
      obj.singleClickEdit = true;
      return obj;
    case "linkedrecordfield":
      obj.cellRenderer = "MultiSelectRenderer";
      return obj;
    case "numericfield":
      obj.valueSetter = _setIntegerValue;
      return obj;
    case "objectfield":
      /** In PRICEBOOK page if its a flat price then the value
       * should be displayed with currency sign prefixed, say "$200", if any
       * other pricing method is selected then the selected type should be
       * displayed, say "Volume Pricing".
       */
      obj.valueFormatter = function (params) {
        if (params.value !== undefined) {
          return params.value.type === PRICING_METHODS.FLAT_PRICE
            ? numberForamting(params.value.value, params.colDef.currencySymbol)
            : PRICING_METHODS_LABELS[params.value.type];
        }
        return null;
      };
      return obj;
    case "percentfield":
      obj.valueFormatter = _formatPercentage;
      obj.valueSetter = _setFloatValue;
      return obj;
    case "singleselectfield":
      obj.cellEditor = "agRichSelectCellEditor";
      obj.cellEditorParams = function () {
        const { values } = item.field.props;
        const isRecurrence = values.includes("Daily");
        if (isRecurrence) {
          return { values: sortArrayAccToMap(values, RECURRENCE) };
        }
        return { values };
      };
      return obj;
    default:
      return obj;
  }
};

/**
 * @param {object} item - Backend Column MetaData.
 * @param {object} props - Empty Object.
 */
export const generateProps = (item, props, type) => {
  let items = [];
  switch (type) {
    case GRID_PAGE_TYPE.DEALTABLE:
      items = [DEALTABLE_COLUMNS.CONFIG_TYPE, DEALTABLE_COLUMNS.PTYPE];
      if (items.includes(item.name)) {
        props[item.name] = item.field.props.values;
      }
      return props;
    case GRID_PAGE_TYPE.PRICELIST:
      items = [
        PRICEBOOK_COLUMNS.BILLING_FREQUENCY,
        PRICEBOOK_COLUMNS.CHARGE_FREQUENCY,
        PRICEBOOK_COLUMNS.CHARGE_TYPE,
        PRICEBOOK_COLUMNS.MAX_USAGE_UOM,
        PRICEBOOK_COLUMNS.PRICE_METHOD,
        PRICEBOOK_COLUMNS.PRODUCT_TYPE,
        PRICEBOOK_COLUMNS.QUANTITY_UOM,
        PRICEBOOK_COLUMNS.RECURRING_PERIOD,
        PRICEBOOK_COLUMNS.UOM,
      ];
      if (items.includes(item.name)) {
        props[item.name] = item.field.props.values;
      }
      return props;
    case GRID_PAGE_TYPE.PRODUCTS:
      items = [PRODUCTS_COLUMNS.TYPE, PRODUCTS_COLUMNS.CONFIG_TYPE];
      if (items.includes(item.name)) {
        props[item.name] = item.field.props.values;
      }
      return props;
    default:
      return props;
  }
};
